import { Controller } from "@hotwired/stimulus"

// The marketplace cards on the COGS page. The row is always full: as many cards as fit on one line, and when they do not all fit
// the last slot is a card-sized switcher whose picker lists every marketplace. The selected marketplace's card always shows,
// then the ones with the most left to do (data-rank, 0 = most). The server renders the choice for a row of 5; this measures the
// real width and keeps it right as the window resizes.
const CARD_REM = 15.5   // .cogs-card-market width
const GAP_REM = 0.75    // gap between cards
const PAD_REM = 0.5     // the row's own side padding, both sides together

export default class extends Controller {
  static targets = ["cards", "card", "picker", "more", "search", "item", "empty"]
  static values = { count: Number }

  connect() {
    this.measure()
    this.observer = new ResizeObserver(() => this.measure())
    this.observer.observe(this.element)
  }

  disconnect() {
    this.observer?.disconnect()
  }

  measure() {
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
    const slots = Math.max(Math.floor((this.element.clientWidth / rem - PAD_REM + GAP_REM) / (CARD_REM + GAP_REM)), 1)
    const cards = this.cardTargets
    const everyCardFits = cards.length <= slots
    // when they do not all fit, one slot goes to the switcher (at least one card always stays)
    const room = everyCardFits ? cards.length : Math.max(slots - 1, 1)

    const ordered = [...cards].sort((a, b) => this.#priority(a) - this.#priority(b))
    const visible = new Set(ordered.slice(0, room))
    cards.forEach((card) => { card.hidden = !visible.has(card) })

    if (this.hasPickerTarget) this.pickerTarget.hidden = everyCardFits
    if (this.hasMoreTarget) this.moreTarget.textContent = `${cards.length - visible.size} more`
  }

  // the selected card first, then by rank
  #priority(card) {
    return card.dataset.selected === "true" ? -1 : Number(card.dataset.rank)
  }

  // Picker: focus the search box when it opens, and start from the full list
  opened() {
    if (!this.hasSearchTarget) return

    this.searchTarget.value = ""
    this.filter()
    this.searchTarget.focus()
  }

  filter() {
    const query = this.searchTarget.value.trim().toLowerCase()
    let visible = 0
    this.itemTargets.forEach((item) => {
      const match = query === "" || query.split(/\s+/).every((word) => item.dataset.name.includes(word))
      item.hidden = !match
      if (match) visible++
    })
    if (this.hasEmptyTarget) this.emptyTarget.hidden = visible > 0
  }

  // Arrow keys move between the search box and the visible rows; Enter follows the focused link as usual.
  keydown(event) {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return

    const rows = this.itemTargets.filter((item) => !item.hidden)
    if (rows.length === 0) return

    event.preventDefault()
    const at = rows.indexOf(document.activeElement)
    if (event.key === "ArrowDown") {
      rows[Math.min(at + 1, rows.length - 1)].focus()
    } else if (at <= 0) {
      this.searchTarget.focus()
    } else {
      rows[at - 1].focus()
    }
  }
}
